import { Box, Tabs } from '@chakra-ui/react';
import { TabBar, TabBarVariant } from 'components/Article/TabBar';
import { WorksheetAnswer } from 'components/worksheet/Review/WorksheetAnswer';
import { WorksheetParticipants } from 'components/worksheet/Review/WorksheetParticipants';
import { useGetWorksheetParticipants } from 'hooks/api/backend/worksheets/useWorksheets';
import { useSearchParams } from 'react-router-dom';
import { DiagramIcon, EditIcon, UsersIcon } from 'assets/icons/v2';
import { WorksheetClassSummary } from 'components/worksheet/Review/WorksheetClassSummary';
import { useFeatureFlagsContext } from 'hooks/utils/useFeatureFlagContext';
import { useUser } from 'utils/auth';
import { canUseWorksheetFeedbackSummary } from 'utils/worksheet/canUseWorksheetFeedbackSummary';
import { ReactElement, useMemo } from 'react';
import { TabData } from 'types/tab';

type Props = {
  worksheetId: string | undefined;
};

export const WorksheetReviewContent = ({ worksheetId }: Props): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEnabled } = useFeatureFlagsContext();
  const { data: user } = useUser();

  const showWorksheetFeedbackSummary = canUseWorksheetFeedbackSummary({
    user,
    isEnabled,
  });
  const { data: worksheetParticipants } = useGetWorksheetParticipants(
    worksheetId,
    searchParams.get('classId') ?? undefined
  );
  const classIdParam = searchParams.get('classId') ?? '';
  const studentIdParam = searchParams.get('studentId') ?? '';

  const tabParam = parseInt(searchParams.get('t') || '0', 10);

  const tabs: TabData[] = useMemo(() => {
    const baseTabs: TabData[] = [
      {
        id: 0,
        icon: <UsersIcon boxSize="16px" color="currentColor" />,
        tabName: 'Teilnehmer:innen',
      },
    ];

    if (showWorksheetFeedbackSummary) {
      baseTabs.push({
        id: 1,
        icon: <DiagramIcon boxSize="16px" color="currentColor" />,
        tabName: 'Kurszusammenfassung',
      });
    }

    baseTabs.push({
      id: showWorksheetFeedbackSummary ? 2 : 1,
      icon: <EditIcon boxSize="16px" color="currentColor" />,
      tabName: 'Antworten',
    });

    return baseTabs;
  }, [showWorksheetFeedbackSummary]);

  const tabsCount = tabs.length;
  const activeTabIndex = tabParam >= 0 && tabParam < tabsCount ? tabParam : 0;

  const handleTabChange = (index: number): void => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('t', index.toString());
    setSearchParams(nextParams);
  };

  return (
    <Box my="30px">
      <TabBar
        px="0"
        variant={TabBarVariant.WORKSHEET}
        tabs={tabs}
        activeTabIndex={activeTabIndex}
        onTabChange={handleTabChange}
      >
        <Tabs.Content value="Teilnehmer:innen" p="20px 0 0">
          {worksheetParticipants && <WorksheetParticipants participants={worksheetParticipants} />}
        </Tabs.Content>
        {showWorksheetFeedbackSummary && (
          <Tabs.Content value="Kurszusammenfassung" p="0">
            <WorksheetClassSummary worksheetId={worksheetId} />
          </Tabs.Content>
        )}
        <Tabs.Content value="Antworten" p="0" pt="l">
          <WorksheetAnswer
            worksheetId={worksheetId}
            initialClassId={classIdParam}
            initialParticipantId={studentIdParam}
          />
        </Tabs.Content>
      </TabBar>
    </Box>
  );
};
